import React, { useState, useEffect, useCallback, useRef } from 'react';
import './ActivitiesTab.css';

const todayISO = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y.slice(2)}`;
};

const WEEKDAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

function StudyPlanTab({ svc }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [title, setTitle] = useState('');
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState(todayISO());
  const [weekdays, setWeekdays] = useState([]);
  const [objetivo, setObjetivo] = useState('');
  const [links, setLinks] = useState(['']);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  // Anotação direto no OneNote (uma caixa aberta por vez).
  const [noteOpenId, setNoteOpenId] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [noteBusy, setNoteBusy] = useState(false);

  // Certificado: um <input type=file> escondido, reaproveitado por todos os cards.
  const certInputRef = useRef(null);
  const certTargetRef = useRef(null);
  const [certBusyId, setCertBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await svc.getStudyPlans();
      if (d.error) throw new Error(d.error);
      setPlans(d.plans || []);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [svc]);

  useEffect(() => { load(); }, [load]);

  const toggleWeekday = (i) => {
    setWeekdays((prev) => prev.includes(i) ? prev.filter((w) => w !== i) : [...prev, i].sort());
  };

  const resetForm = () => {
    setEditingId(null);
    setTitle(''); setName(''); setObjetivo(''); setLinks(['']); setWeekdays([]);
    setStartDate(todayISO()); setEndDate(todayISO());
  };

  const closeForm = () => { resetForm(); setShowForm(false); };

  const openNew = () => {
    resetForm(); setError(null); setInfo(null);
    setShowForm(true);
  };

  const startEdit = (p) => {
    setShowForm(true);
    setEditingId(p.id);
    setTitle(p.title || ''); setName(p.name || ''); setObjetivo(p.notes || '');
    setLinks(p.links && p.links.length ? p.links : ['']);
    setWeekdays(p.weekdays || []);
    setStartDate(p.start_date); setEndDate(p.end_date);
    setError(null); setInfo(null);
  };

  const save = async () => {
    if (!name.trim() || weekdays.length === 0) return;
    setBusy(true); setError(null); setInfo(null);
    const plano = {
      title: title.trim(), name: name.trim(), start_date: startDate, end_date: endDate,
      weekdays, notes: objetivo.trim(),
      links: links.map((l) => l.trim()).filter(Boolean),
    };
    try {
      const d = editingId
        ? await svc.updateStudyPlan({ id: editingId, ...plano })
        : await svc.createStudyPlan(plano);
      if (d.error) throw new Error(d.error);
      if (d.onenote_error) {
        setInfo(`Plano salvo, mas a página do OneNote não foi criada: ${d.onenote_error}`);
      } else if (d.onenote_url || !editingId) {
        setInfo(editingId ? '✓ Plano atualizado.' : '✓ Plano salvo e página criada no OneNote.');
      } else {
        setInfo('✓ Plano atualizado.');
      }
      closeForm();
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const remove = async (p) => {
    try {
      await svc.deleteStudyPlan(p.id);
      if (editingId === p.id) closeForm();
      await load();
    } catch (e) { setError(e.message); }
  };

  const pickCertificate = (p) => {
    certTargetRef.current = p.id;
    certInputRef.current?.click();
  };

  const onCertificateChosen = async (e) => {
    const file = e.target.files?.[0];
    const id = certTargetRef.current;
    e.target.value = '';
    if (!file || !id) return;
    if (file.size > 10 * 1024 * 1024) { setError('Certificado maior que 10 MB.'); return; }
    setCertBusyId(id); setError(null); setInfo(null);
    try {
      const d = await svc.uploadStudyCertificate(id, file);
      if (d.error) throw new Error(d.error);
      setInfo('✓ Certificado anexado.');
      await load();
    } catch (err) { setError(err.message); } finally { setCertBusyId(null); }
  };

  const removeCertificate = async (p) => {
    if (!window.confirm('Remover o certificado deste plano?')) return;
    setCertBusyId(p.id); setError(null); setInfo(null);
    try {
      const d = await svc.deleteStudyCertificate(p.id);
      if (d.error) throw new Error(d.error);
      await load();
    } catch (err) { setError(err.message); } finally { setCertBusyId(null); }
  };

  const saveNote = async (p) => {
    if (!noteText.trim()) return;
    setNoteBusy(true); setError(null); setInfo(null);
    try {
      const d = await svc.addStudyPlanNote(p.id, noteText.trim());
      if (d.error) throw new Error(d.error);
      setInfo('✓ Anotação salva no OneNote.');
      setNoteText(''); setNoteOpenId(null);
      await load();
    } catch (e) { setError(e.message); } finally { setNoteBusy(false); }
  };

  return (
    <div className="ac-view">
      <div className="ac-form">
        <button className="ac-add" onClick={openNew} style={{ flex: 1 }}>＋ Novo plano de estudo</button>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => { if (!busy) closeForm(); }}>
          <div
            className="modal" onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: '90vh', overflowY: 'auto' }}
          >
            <h3 className="modal-title">{editingId ? 'Editar plano de estudo' : 'Novo plano de estudo'}</h3>
      <div className="ac-form" style={{ flexWrap: 'wrap' }}>
        <input
          className="ac-input ac-input-name"
          placeholder="Título (nome da página no OneNote)…"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div className="ac-form" style={{ flexWrap: 'wrap' }}>
        <input
          className="ac-input ac-input-name"
          placeholder="Nome do plano de estudo…"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input className="ac-input ac-input-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <input className="ac-input ac-input-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '10px 4px' }}>
        {WEEKDAYS.map((label, i) => (
          <button
            key={i}
            onClick={() => toggleWeekday(i)}
            style={{
              padding: '8px 12px', borderRadius: 999, fontSize: 13, cursor: 'pointer',
              border: weekdays.includes(i) ? '1px solid var(--accent-primary)' : '1px solid var(--border-color)',
              background: weekdays.includes(i) ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
              color: '#fff',
            }}
          >{label}</button>
        ))}
      </div>

      <div className="ac-form">
        <input
          className="ac-input ac-input-name"
          placeholder="Objetivo (opcional)…"
          value={objetivo}
          onChange={(e) => setObjetivo(e.target.value)}
        />
      </div>

      {links.map((l, i) => (
        <div className="ac-form" key={i} style={{ gap: 6 }}>
          <input
            className="ac-input ac-input-name"
            type="url"
            inputMode="url"
            autoCapitalize="none"
            placeholder={i === 0 ? 'Link (opcional)…' : 'Outro link…'}
            value={l}
            onChange={(e) => setLinks((prev) => prev.map((x, k) => (k === i ? e.target.value : x)))}
          />
          {links.length > 1 && (
            <button
              className="ac-del" aria-label="Remover link" title="Remover link"
              onClick={() => setLinks((prev) => prev.filter((_, k) => k !== i))}
            >✕</button>
          )}
        </div>
      ))}
      {links.length < 10 && (
        <div style={{ margin: '0 4px 8px' }}>
          <button
            onClick={() => setLinks((prev) => [...prev, ''])}
            style={{ fontSize: 12, background: 'none', border: 'none', color: 'var(--accent-primary)', cursor: 'pointer', padding: 0 }}
          >＋ Adicionar outro link</button>
        </div>
      )}

      <div className="ac-form" style={{ gap: 8 }}>
        <button className="ac-add" onClick={save} disabled={busy || !name.trim() || weekdays.length === 0} style={{ flex: 1 }}>
          {busy ? '…' : editingId ? 'Salvar alterações' : 'Cadastrar plano de estudo'}
        </button>
        {(
          <button className="ac-add" onClick={closeForm} disabled={busy} style={{ flex: 'none', background: 'var(--bg-tertiary)' }}>
            Cancelar
          </button>
        )}
      </div>

            {error && <p className="ac-msg ac-err">{error}</p>}
          </div>
        </div>
      )}

      <input
        ref={certInputRef} type="file" style={{ display: 'none' }}
        accept="application/pdf,image/png,image/jpeg,image/webp,.pdf,.png,.jpg,.jpeg,.webp"
        onChange={onCertificateChosen}
      />

      {!showForm && error && <p className="ac-msg ac-err">{error}</p>}
      {info && <p className="ac-msg">{info}</p>}
      {loading && plans.length === 0 && <p className="ac-msg">Carregando…</p>}
      {!loading && plans.length === 0 && <p className="ac-msg">Nenhum plano de estudo cadastrado ainda.</p>}

      {plans.map((p) => (
        <div className="ac-group" key={p.id}>
          <div className="ac-item">
            <span className="ac-name">
              {p.title && <div style={{ fontWeight: 700 }}>{p.title}</div>}
              <div style={p.title ? { fontSize: 13, color: 'var(--text-secondary)' } : undefined}>{p.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                {fmtDate(p.start_date)} – {fmtDate(p.end_date)} · {p.weekdays.map((w) => WEEKDAYS[w]).join(', ')}
              </div>
              {p.notes && (
                <div style={{ fontSize: 12, color: 'var(--accent-primary)', marginTop: 4 }}>
                  🎯 {p.notes}
                </div>
              )}
              {(p.links || []).map((l) => (
                <div key={l} style={{ marginTop: 6 }}>
                  <a
                    href={l} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 12, color: 'var(--accent-primary)', wordBreak: 'break-all' }}
                  >
                    🔗 {l.replace(/^https?:\/\//, '')}
                  </a>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
                {p.has_certificate ? (
                  <>
                    <a
                      href={svc.studyCertificateUrl(p.id)} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 12, color: 'var(--accent-primary)' }}
                    >
                      🎓 Ver certificado
                    </a>
                    <button
                      onClick={() => pickCertificate(p)} disabled={certBusyId === p.id}
                      style={{ fontSize: 12, background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 0 }}
                    >
                      {certBusyId === p.id ? '…' : 'trocar'}
                    </button>
                    <button
                      onClick={() => removeCertificate(p)} disabled={certBusyId === p.id}
                      style={{ fontSize: 12, background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 0 }}
                    >
                      remover
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => pickCertificate(p)} disabled={certBusyId === p.id}
                    style={{ fontSize: 12, background: 'none', border: '1px solid var(--border-color)', borderRadius: 999, color: 'var(--accent-primary)', cursor: 'pointer', padding: '4px 10px' }}
                  >
                    {certBusyId === p.id ? 'Enviando…' : '🎓 Certificado'}
                  </button>
                )}
              </div>
              {p.onenote_url && (
                <a
                  href={p.onenote_url} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-block', fontSize: 12, marginTop: 6, color: 'var(--accent-primary)' }}
                >
                  ↗ Abrir no OneNote
                </a>
              )}
            </span>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button
                className="ac-del" onClick={() => { setNoteOpenId(noteOpenId === p.id ? null : p.id); setNoteText(''); }}
                aria-label="Anotar no OneNote" title="Anotar no OneNote"
              >📝</button>
              <button className="ac-del" onClick={() => startEdit(p)} aria-label="Editar" title="Editar">✎</button>
              <button className="ac-del" onClick={() => remove(p)} aria-label="Excluir">✕</button>
            </div>
          </div>

          {noteOpenId === p.id && (
            <div style={{ padding: '8px 4px 4px' }}>
              <textarea
                className="ac-input"
                style={{ width: '100%', minHeight: 90, resize: 'vertical', boxSizing: 'border-box' }}
                placeholder="Escreva aqui — vai direto pra página do OneNote…"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                autoFocus
              />
              <button
                className="ac-add" style={{ width: '100%', marginTop: 8 }}
                onClick={() => saveNote(p)} disabled={noteBusy || !noteText.trim()}
              >
                {noteBusy ? '…' : 'Salvar no OneNote'}
              </button>
            </div>
          )}
        </div>
      ))}
      <div className="ac-spacer" />
    </div>
  );
}

export default StudyPlanTab;
